import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
  OnInit,
} from '@angular/core';
import { ArgoVideoAccess, ArgoVideoShort, CommunityItem, IPartnerService, WatchProgress } from '../../../../interfaces';
import { SwiperOptions } from 'swiper';
import { debounceTime, finalize, firstValueFrom, map, take, tap } from 'rxjs';
import { VideoService } from '../../../../services/api/video.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { OUTLAW_QUERY_PARAMS, User } from '@outlaw/core';
import { FullScreenPlayService } from '../../../../services/util/full-screen-play.service';
import {
  CommunityNavigationService,
  FeatureFlagService,
  GuestAuthService,
  CommunityMobileEventsService,
  PlaylistVideosService,
} from '../../../../services';
import { StreamComponent } from '../../../stream/stream.component';
import { PostTextComponent } from '../post-text/post-text.component';
import { CommunityLoaderComponent } from '../../../community-loader/community-loader.component';
import { SubscribeButtonComponent } from '../../../subscribe-button/subscribe-button.component';
import { PlaylistCarouselComponent } from '../../../playlist-carousel/playlist-carousel.component';
import { PartnerServiceFactory } from '../../../../services/partner-services/partner-service.factory';
import { TextContentComponent } from '../text-content/text-content.component';
import { IconComponent } from '@outlaw/icons';

type SubscriptionParams = {
  isFreeVideo: boolean;
  isGuest: boolean;
  isMajiApp: boolean;
  isMobile: boolean;
};

@Component({
  selector: 'outlaw-post-playlist',
  templateUrl: './post-playlist.component.html',
  styleUrls: ['./post-playlist.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PostTextComponent,
    CommunityLoaderComponent,
    SubscribeButtonComponent,
    StreamComponent,
    PlaylistCarouselComponent,
    TextContentComponent,
    IconComponent,
  ],
})
export class PostPlaylistComponent implements OnInit {
  item = input<CommunityItem>();
  isGuest = input<boolean>(false);
  isMajiApp = input<boolean>(false);
  variant = input<'primary' | 'secondary'>('primary');
  openInSameWindow = input<boolean>(false);
  loadingChange = output<boolean>();
  watchMoreOpenDialog = output<void>();
  navigateToPost = output<void>();

  overrideUrl = computed(() => {
    return this.variant() === 'primary' ? this.communityNavigationService.getPostUrl(this.item().post_uuid) : null;
  });

  @ViewChild(StreamComponent) streamComponent: StreamComponent;

  carouselConfig: SwiperOptions = {
    navigation: false,
    spaceBetween: 12,
  };

  selectedVideo: ArgoVideoShort | null = null;
  selectedVideoAccess: ArgoVideoAccess | null = null;
  videos: ArgoVideoShort[] = [];

  streamUrl: string | null = null;
  mutedTrailer = true;
  user: User | null = null;
  loading = signal(false);
  loadingAccess = signal(false);
  activatePlay = signal(true);
  hasSubscription = signal(false);

  private videoClickSubject = new Subject<ArgoVideoShort>();
  private wasPopupShown = false;
  private readonly PROGRESS_THRESHOLD = 70;

  get preview(): string | null {
    return this.selectedVideo ? this.selectedVideo.thumbnail_url : this.item().content.images?.[0]?.image_url;
  }

  get trailerToken(): string | null {
    return this.selectedVideo?.trailer || null;
  }

  get hasTrailerToken(): boolean {
    return !!this.trailerToken;
  }

  get canDisplayControls(): boolean {
    return this.hasVideoToken;
  }

  get hasUser(): boolean {
    return !this.isGuest();
  }

  get activeVideoToken(): string {
    return this.streamUrl || this.trailerToken;
  }

  get hasVideoToken(): boolean {
    return !!this.streamUrl;
  }

  get canDisplayTrailerButton(): boolean {
    return this.hasUser && this.hasVideoToken && this.hasTrailerToken;
  }

  get isFreeVideo(): boolean {
    return !this.hasUser && this.selectedVideo?.is_free;
  }

  get canDisplayPreview(): boolean {
    if (this.hasTrailerToken) {
      return false;
    }

    if (this.isGuest()) {
      return true;
    }

    return !this.hasVideoToken;
  }

  get requiresSubscription(): boolean {
    return !this.hasUser || (this.selectedVideoAccess && !this.selectedVideoAccess.have_access);
  }

  get poster(): string {
    return this.selectedVideo?.thumbnail_url || '';
  }

  get isVideoMuted(): boolean {
    return !this.streamUrl && this.mutedTrailer;
  }

  get isPrimaryView(): boolean {
    return this.variant() === 'primary';
  }

  get isNewPlaylistEnabled(): boolean {
    return this.featureFlagService.isNewPlaylistEnabled();
  }

  get loopVideo(): boolean {
    return !this.hasUser || !(this.hasUser && this.selectedVideo?.is_free);
  }

  get isMobile(): boolean {
    return this.mobileEventsService.isWebView;
  }

  private playlistVideosService = inject(PlaylistVideosService);
  private videoService = inject(VideoService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private fullScreenPlayService = inject(FullScreenPlayService);
  private featureFlagService = inject(FeatureFlagService);
  private partnerServiceFactory = inject(PartnerServiceFactory);
  private mobileEventsService = inject(CommunityMobileEventsService);
  private guestAuthService = inject(GuestAuthService);

  private partnerService: IPartnerService;
  private communityNavigationService = inject(CommunityNavigationService);

  ngOnInit() {
    if (this.variant() === 'primary') {
      return;
    }

    this.observeVideoClick();
    this.observeFullscreenPlayerState();
    this.getVideos(this.isGuest());
    this.partnerService = this.partnerServiceFactory.getService(this.item().original_platform.slug);
  }

  onVideoClick(video: ArgoVideoShort) {
    this.loadingAccess.set(true);
    this.videoClickSubject.next(video);
  }

  toggleFullscreen(videoPlayerData: ArgoVideoShort, isMajiApp: boolean, isMobile: boolean) {
    const videoToken = videoPlayerData.trailer;
    const title = videoPlayerData.name;

    if (isMajiApp && isMobile) {
      this.mobileEventsService.openVideo({ token: videoToken, title });
      return;
    }

    this.fullScreenPlayService.startFullScreenPlay({
      title,
      streamUrl: videoToken,
      thumbnail: videoPlayerData.thumbnail_url,
    });
  }

  onVideoFinished(selectedVideo: ArgoVideoShort, hasSubscription: boolean): void {
    const canOpenWatchMoreDialog = this.hasUser && selectedVideo && selectedVideo.is_free && !hasSubscription;

    if (canOpenWatchMoreDialog) {
      this.watchMoreOpenDialog.emit();
    }
  }

  onSubscribeClick({ isFreeVideo, isGuest, isMajiApp, isMobile }: SubscriptionParams): void {
    if (!isGuest) {
      if (isMajiApp && isMobile) {
        this.mobileEventsService.openSubscribe();
        return;
      }

      if (this.selectedVideo) {
        this.communityNavigationService.navigateToSubscription({
          [OUTLAW_QUERY_PARAMS.filmID]: this.selectedVideo.id.toString(),
          [OUTLAW_QUERY_PARAMS.postId]: this.item().post_uuid,
        });

        return;
      }
    }

    this.guestAuthService.handleGuestAuth({
      usePassportRedirect: true,
      filmId: isFreeVideo ? this.item().post_uuid : undefined,
      returnSubscribePage: !isFreeVideo ? this.item().post_uuid : undefined,
    });
  }

  onVideoProgress(progress: number): void {
    if (
      !this.wasPopupShown &&
      this.hasUser &&
      this.selectedVideo?.is_free &&
      !this.hasSubscription() &&
      progress >= this.PROGRESS_THRESHOLD
    ) {
      this.watchMoreOpenDialog.emit();
      this.wasPopupShown = true;
    }
  }

  async addWatchProgress(progress: number): Promise<void> {
    if (this.isGuest() || !this.partnerService) {
      return;
    }

    const watchProgress: WatchProgress = { progress };

    await firstValueFrom(this.partnerService.addWatchProgressToVideo(this.selectedVideo.id, watchProgress));
  }

  private getVideos(isGuest: boolean): void {
    const playlistId = +this.item().content?.repost?.original?.original_id || null;

    if (!playlistId || isNaN(playlistId)) {
      this.loading.set(false);
      this.loadingChange.emit(false);
      this.cdr.markForCheck();
      return;
    }

    const playlistRequest = isGuest
      ? this.playlistVideosService.getPlaylistVideosGuest(playlistId)
      : this.playlistVideosService.getPlaylistVideos(playlistId).pipe(
          tap(({ hasSubscription }) => this.hasSubscription.set(hasSubscription)),
          map(({ videos }) => videos)
        );

    this.loading.set(true);
    this.loadingChange.emit(true);

    playlistRequest.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: videos => {
        if (videos.length === 0) {
          this.videos = [];
          this.loading.set(false);
          this.loadingChange.emit(false);
          this.cdr.markForCheck();
          return;
        }

        this.videos = videos;

        const firstVideo = videos[0];

        if (firstVideo) {
          this.onVideoClick(firstVideo);
        }
      },
      error: error => {
        console.error('Error fetching playlist', error);
        this.videos = [];
      },
    });
  }

  private observeVideoClick(): void {
    this.videoClickSubject.pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef)).subscribe(video => {
      this.processVideoClick(video);
    });
  }

  private processVideoClick(video: ArgoVideoShort) {
    if (this.selectedVideo?.id === video.id) {
      return;
    }

    const previousVideo = this.selectedVideo;
    this.selectedVideo = video;
    this.selectedVideoAccess = null;
    this.streamUrl = null;
    this.cdr.markForCheck();

    if (this.isGuest()) {
      this.loading.set(false);
      this.loadingAccess.set(false);
      this.loadingChange.emit(false);
      return;
    }

    if (previousVideo?.is_free && !this.hasSubscription() && this.hasUser && !this.wasPopupShown) {
      this.watchMoreOpenDialog.emit();
      this.wasPopupShown = true;
    }

    this.hasUserAccessToVideo(video);
  }

  private hasUserAccessToVideo(video: ArgoVideoShort): void {
    this.videoService
      .hasArgoVideoAccess(video.id)
      .pipe(
        finalize(() => {
          this.loadingAccess.set(false);
          this.loading.set(false);
          this.loadingChange.emit(false);
          this.cdr.markForCheck();
        }),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (hasAccess: ArgoVideoAccess) => {
          this.selectedVideoAccess = hasAccess;
          this.streamUrl = hasAccess.stream_url;
          this.cdr.markForCheck();
        },
        error: error => {
          console.error('Error fetching video access', error);
        },
      });
  }

  private observeFullscreenPlayerState(): void {
    this.fullScreenPlayService.isActivePlay.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isActive => {
      this.activatePlay.set(!isActive);
    });
  }

  onPostClick(): void {
    this.navigateToPost.emit();
  }
}
